// Longform hierarchy (Owner order 2026-09-17, D-120): an EPISODE card never appears as a
// top-level entry where a Skit card is a peer. Every list that shows cards runs its
// records through groupEpisodes() and renders ONE series entry with the episodes nested.
//
// The marker is model-true: only the two Longform episode-creation paths write
// live_production.episode_number, so `episodeNumber != null` (with a script) = episode card.
// Riffs, Skits, Mocks, Cooking cards pass through untouched, in their original order.

export const isEpisodeCard = (rec) => rec != null && rec.episodeNumber != null && !!(rec.scriptId || rec.script?.id);

export const LIVE_STAGE = { Scripted: 'Script', Filming: 'Shoot', Filmed: 'Shoot', Editing: 'Edit', Incomplete: 'Shoot', Qual: 'Review', Approved: 'Review', Posted: 'Post', Archived: 'Post' };
const STAGE_RANK = { Scripted: 0, Filming: 1, Incomplete: 1, Filmed: 2, Editing: 3, Qual: 4, Approved: 5, Posted: 6, Archived: 6 };

/** Aggregate progress for a series' episodes: "3/8 Posted · next Ep 4 — Edit". */
export function seriesProgress(episodes) {
  const eps = [...(episodes || [])].sort((a, b) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0));
  const total = eps.length;
  const posted = eps.filter(e => e.status === 'Posted' || e.status === 'Archived').length;
  const next = eps.find(e => e.status !== 'Posted' && e.status !== 'Archived') || null;
  const furthest = eps.reduce((m, e) => (STAGE_RANK[e.status] ?? 0) > (STAGE_RANK[m?.status] ?? -1) ? e : m, null);
  const label = `${posted}/${total} Posted` + (next ? ` · next Ep ${next.episodeNumber} — ${next.productionActive === false ? 'Planned' : (LIVE_STAGE[next.status] || next.status)}` : total ? ' · all posted' : '');
  return { total, posted, next, furthest, label, allPosted: total > 0 && posted === total };
}

/**
 * records → entries. Non-episode cards: { kind: 'card', record }. Episode cards of one
 * script: ONE { kind: 'series', key, scriptId, scriptName, client, episodes, progress, record }
 * placed where the series' first episode sat in the input order; `record` is the
 * representative episode (the "next" one, else the first) so existing per-record renderers
 * (client, thumbnail, talent) have something to show at the series level.
 */
export function groupEpisodes(records) {
  const out = []; const bySeries = new Map();
  for (const rec of records || []) {
    if (!isEpisodeCard(rec)) { out.push({ kind: 'card', key: rec.id, record: rec }); continue; }
    const scriptId = rec.scriptId || rec.script?.id;
    let entry = bySeries.get(scriptId);
    if (!entry) {
      entry = { kind: 'series', key: `series:${scriptId}`, scriptId, scriptName: rec.script?.scriptName || '(series)', client: rec.client || null, episodes: [], progress: null, record: rec };
      bySeries.set(scriptId, entry); out.push(entry);
    }
    entry.episodes.push(rec);
  }
  for (const entry of bySeries.values()) {
    entry.episodes.sort((a, b) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0));
    entry.progress = seriesProgress(entry.episodes);
    entry.record = entry.progress.next || entry.episodes[0];
  }
  return out;
}

/** A text filter matches a series entry if ANY episode matches; returns the matching episode ids. */
export function seriesMatches(entry, predicate) {
  if (entry.kind !== 'series') return { matches: !!predicate(entry.record), episodeIds: [] };
  const ids = entry.episodes.filter(predicate).map(e => e.id);
  return { matches: ids.length > 0, episodeIds: ids };
}

/** Flatten grouped entries back to records (series → its episodes) — for bulk actions and counts. */
export function flattenEntries(entries) {
  return (entries || []).flatMap(e => e.kind === 'series' ? e.episodes : [e.record]);
}
